import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { getDefaultPayinMethod } from '@/features/memberArea/payments/payments.helpers'
import { useShowAppError } from '@/services/appErrors/appErrorAtom'
import { BankIdState } from '@/services/bankId/bankId.types'
import { useBankIdContext } from '@/services/bankId/BankIdContext'
import {
  type ShopSessionFragment,
  useCarDealershipRemoveAddMutation,
  useCurrentMemberLazyQuery,
} from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'

const LOGGER = logger.createLogger({ name: 'car-dealership' })

type Params = {
  shopSession: ShopSessionFragment
  trialContractId: string
  requirePaymentConnection: boolean
}

export const useAcceptExtension = (params: Params) => {
  const { startCheckoutSign, currentOperation, dispatch } = useBankIdContext()
  const router = useRouter()
  const showError = useShowAppError()
  const [getCurrentMember] = useCurrentMemberLazyQuery()
  const locale = useRoutingLocale()

  useEffect(() => {
    const error = currentOperation?.error
    if (error) {
      showError(new Error(error))
    }
  }, [showError, currentOperation])

  const performSign = () => {
    const { ssn } = params.shopSession.customer ?? {}

    if (!ssn) {
      return LOGGER.info(`Impossible to sign shopSession: ${params.shopSession.id} - lacking 'ssn'`)
    }

    startCheckoutSign({
      shopSessionId: params.shopSession.id,
      ssn,

      async onSuccess() {
        const { data, error } = await getCurrentMember()

        if (!data) {
          showError(error ?? new Error('Failed to fetch current member'))
          return
        }

        const nextUrl = PageLink.carDealershipConfirmation({
          locale,
          contractId: params.trialContractId,
        }).pathname

        dispatch({ type: 'success' })

        const hasPayinMethod = Boolean(getDefaultPayinMethod(data.currentMember.paymentMethods))

        if (!hasPayinMethod) {
          LOGGER.info('Member does not have active payment connection', {
            promptedPayment: params.requirePaymentConnection,
          })
          router.push(
            PageLink.checkoutPaymentTrustly({
              locale,
              shopSessionId: params.shopSession.id,
              nextUrl,
            }).href,
          )
        } else {
          LOGGER.info('Member has active payment connection', {
            promptedPayment: params.requirePaymentConnection,
          })
          router.push(nextUrl)
        }
      },
    })
  }

  const [cartRemoveAdd, { loading: loadingRemoveAdd }] = useCarDealershipRemoveAddMutation({
    onCompleted() {
      LOGGER.info('Offer already added to cart')
      performSign()
    },
    onError: showError,
  })

  const addExtensionOffer = (offerId: string) => {
    const cartOfferIds = params.shopSession.cart.items.map((item) => item.productOffer.id)
    const removeOfferIds = cartOfferIds.filter((id) => id !== offerId)
    const addOfferIds = cartOfferIds.includes(offerId) ? [] : [offerId]

    if (removeOfferIds.length > 0) {
      LOGGER.info('Removing offers from cart', { offers: removeOfferIds })
    }

    if (addOfferIds.length > 0) {
      LOGGER.info('Adding offers to cart', { offers: addOfferIds })
    }

    if (removeOfferIds.length === 0 && addOfferIds.length === 0) {
      LOGGER.info('Offer already added to cart')
      performSign()
      return
    }

    cartRemoveAdd({
      variables: {
        shopSessionId: params.shopSession.id,
        removeOfferIds,
        addOfferIds,
      },
    })
  }

  let signLoading = false
  const { state: bankIdState } = currentOperation ?? {}
  if (bankIdState) {
    signLoading = [BankIdState.Starting, BankIdState.Pending, BankIdState.Success].includes(bankIdState)
  }
  const loading = loadingRemoveAdd || signLoading

  return [addExtensionOffer, loading] as const
}
